/**
 * Configuración de voces por modo y acento
 * 
 * Este archivo centraliza todas las configuraciones de voces para los diferentes
 * modos de práctica (Galia, Tommy, etc.) y acentos (Francia, Canadá, Australia, Bélgica).
 * 
 * Para cambiar una voz, simplemente modifica el valor de 'voice' en la combinación deseada.
 * El formato de voz debe ser compatible con Azure Cognitive Services (ej: 'fr-FR-Chirp3-HD-Achernar')
 */

import { PracticeMode, AccentType } from '../types';

export interface VoiceConfig {
  voice: string;
  language: string;
}

// SPRINT GALIA 1.5 (Santiago, 2026-05-14):
// Forzamos voz EUROPEA en TODAS las combinaciones modo×acento. Las variantes
// regionales (fr-CA, fr-BE) están desactivadas hasta resolver la contaminación
// reportada por el cliente. El usuario puede seguir eligiendo "Canadá"/"Bélgica"
// en el picker, pero técnicamente Galia sirve fr-FR. Ver comentario en
// ACCENT_MODIFIERS (src/config/prompts.ts).
const EU_VOICE: VoiceConfig = {
  voice: 'fr-FR-Chirp3-HD-Achernar',
  language: 'fr-FR'
};

const ALL_EU_ACCENTS: Record<AccentType, VoiceConfig> = {
  'Francia': EU_VOICE,
  'Canadá': EU_VOICE,
  'Australia': EU_VOICE,
  'Bélgica': EU_VOICE,
};

// Configuración de voces: [modo][acento] → { voice, language }
export const VOICE_CONFIG: Record<PracticeMode, Record<AccentType, VoiceConfig>> = {
  'conversacional': ALL_EU_ACCENTS,
  'estudio': ALL_EU_ACCENTS,
  'examen': ALL_EU_ACCENTS,
  'actividades-docente': ALL_EU_ACCENTS,
  'actividades-estudiante': ALL_EU_ACCENTS,
};

/**
 * Obtiene la configuración de voz para un modo y acento específicos
 * 
 * @param mode - Modo de práctica (conversacional, estudio, examen, etc.)
 * @param accent - Acento seleccionado (Francia, Canadá, Australia, Bélgica)
 * @returns Configuración de voz con voice y language, o valores por defecto si no se encuentra
 */
export function getVoiceConfig(mode: PracticeMode, accent: AccentType): VoiceConfig {
  // Verificar que la configuración existe
  if (VOICE_CONFIG[mode] && VOICE_CONFIG[mode][accent]) {
    return VOICE_CONFIG[mode][accent];
  }

  // Valores por defecto si no se encuentra la combinación
  console.warn(`[VoiceConfig] No se encontró configuración para modo "${mode}" y acento "${accent}", usando valores por defecto`);
  return {
    voice: 'fr-FR-Chirp3-HD-Achernar',
    language: 'fr-FR'
  };
}


